import React, { useEffect, useRef } from 'react';
import { GatsbyImage, withArtDirection } from 'gatsby-plugin-image';
import { getGatsbyImage } from 'gatsby-plugin-storyblok-image';
import throttle from 'lodash/throttle';
import { arrayOf, bool, object } from 'prop-types';
import clsx from 'clsx';
import { useViewport } from '../../../context/viewport.context';
import { bpWidth, sizes } from '../../../styles/variables';

// this component is a refactor of BottomCarousel
const HorizontalScroller = ({ images, useNavigation = true, fullWidth }) => {
  const { viewWidth } = useViewport();

  // Drag & scroll with mouse
  const slidesRef = useRef(null);
  const scrollThumb = useRef(null);
  let clickedDown = false;
  let startX;
  let scrollLeft;

  const setThumbSize = () => {
    if (useNavigation) {
      scrollThumb.current.style.width = `${(slidesRef.current.clientWidth / (slidesRef.current.scrollWidth || 1)) * 100}%`;
    }
  };

  // handle event listeners
  useEffect(() => {
    const observer = new ResizeObserver(setThumbSize);
    const slidesEl = slidesRef.current;
    const thumbEl = scrollThumb.current;

    const mouseDownHandler = (e) => {
      clickedDown = true;
      ({ scrollLeft } = slidesEl);
      slidesEl.classList.add('!cursor-grabbing');
      startX = e.pageX - slidesEl.offsetLeft;
    };

    const mouseLeaveHandler = () => {
      clickedDown = false;
      slidesEl.classList.remove('!cursor-grabbing');
    };

    const mouseMoveHandler = (e) => {
      if (clickedDown) {
        e.preventDefault();
        const currentX = e.pageX - slidesEl.offsetLeft;
        const scroll = currentX - startX;
        slidesEl.scrollLeft = scrollLeft - scroll;

        if (useNavigation) {
          thumbEl.style.left = `${(slidesEl.scrollLeft / slidesEl.scrollWidth) * 100}%`;
        }
      }
    };

    const scrollHandler = () => {
      thumbEl.style.left = `${(slidesEl.scrollLeft / slidesEl.scrollWidth) * 100}%`;
    };

    setThumbSize();

    // Set up event listeners
    const throttledMouseMoveHandler = throttle(mouseMoveHandler, 10);

    if (slidesEl) {
      slidesEl.addEventListener('mousedown', mouseDownHandler);
      slidesEl.addEventListener('mouseleave', mouseLeaveHandler);
      slidesEl.addEventListener('mouseup', mouseLeaveHandler);
      slidesEl.addEventListener('mousemove', throttledMouseMoveHandler);
      slidesEl.addEventListener('scroll', scrollHandler);
      observer.observe(slidesEl);
    }

    // Remove event listeners
    return () => {
      if (slidesEl) {
        slidesEl.removeEventListener('mousedown', mouseDownHandler);
        slidesEl.removeEventListener('mouseleave', mouseLeaveHandler);
        slidesEl.removeEventListener('mouseup', mouseLeaveHandler);
        slidesEl.removeEventListener('mousemove', throttledMouseMoveHandler);
        slidesEl.removeEventListener('scroll', scrollHandler);
        observer.disconnect();
      }
    };
  });

  return (
    // row
    <div
      className={clsx(
        'max-w-full box-border flex flex-wrap flex-initial mx-0',
        fullWidth && 'ml-[30px] sm:ml-[12.5vw] md:ml-[8.3vw]',
      )}
      data-cy="slides-row"
    >
      {/* col */}
      <div className="box-border block max-w-[100%] basis-[100%] grow-0 shrink-0 px-0" data-cy="slides-col">
        {/* slides container outer, formerly SlidesCol */}
        <div
          className={clsx(
            'flex cursor-pointer flex-row w-full max-w-[100vw]',
            'transition-all duration-[.2s] [&_.active]:cursor-grabbing',
            useNavigation ? 'overflow-x-scroll [scrollbar-width:none]' : 'overflow-x-scroll overflow-y-hidden',
          )}
          ref={slidesRef}
          data-cy="slides-container-outer"
        >
          {/* slides container inner, formerly Slides */}
          <div
            className={clsx(
              'flex flex-row max-h-[300px] sm:max-h-[400px] lg:max-h-[500px] select-none',
              !useNavigation && 'pb-[20px] sm:pb-[30px] lg:pb-[40px]',
            )}
            data-cy="slides-container-inner"
          >
            {images.map((image) => {
              if (!image.assetLg?.filename) {
                return undefined;
              }

              const loadType = 'lazy';

              let imageUrl =
                image.assetLg?.filename ||
                image.assetMd?.filename ||
                image.assetSm?.filename ||
                image.assetXs?.filename;

              if (viewWidth < sizes.containerMinWidth.tablet) {
                if (image.assetXs?.filename) {
                  imageUrl = image.assetXs.filename;
                }
              } else if (viewWidth < sizes.containerMinWidth.desktopSm) {
                if (image.assetSm?.filename) {
                  imageUrl = image.assetSm.filename;
                }
              } else if (viewWidth < sizes.containerMinWidth.desktopLg) {
                if (image.assetMd?.filename) {
                  imageUrl = image.assetMd.filename;
                }
              }

              /** @type { GetGatsbyImageOptions } */
              const options = { layout: image.layout, quality: image.quality };

              const artDirected = [];
              if (image.assetXs?.filename) {
                artDirected.push({
                  media: `(${bpWidth.mobile})`,
                  image: getGatsbyImage(image.assetXs.filename, options),
                });
              }

              if (image.assetSm?.filename) {
                artDirected.push({
                  media: `(${bpWidth.tablet})`,
                  image: getGatsbyImage(image.assetSm.filename, options),
                });
              }

              if (image.assetMd?.filename) {
                artDirected.push({
                  media: `(${bpWidth.desktopSm})`,
                  image: getGatsbyImage(image.assetMd.filename, options),
                });
              }

              const imageData = withArtDirection(getGatsbyImage(imageUrl, options), artDirected);
              const media = <GatsbyImage image={imageData} loading={loadType} alt="" className="slideImage" />;

              return (
                <div
                  className={clsx(
                    fullWidth
                      ? `w-[300px] sm:w-[400px] lg:w-[500px] last:mr-[12.5vw] md:last:mr-[10vw]
                        lg:last:mr-[8.333333333333334vw]`
                      : 'w-[308px] sm:w-[370px] last:mr-0',
                    `mr-[10px] sm:mr-[16px] md:mr-[20px] lg:mr-[24px] [&_.gatsby-image-wrapper-constrained]:h-full
                    [&_.gatsby-image-wrapper-constrained]:w-full [&_img]:pointer-events-none`,
                  )}
                  key={image._uid}
                  data-cy="slide"
                >
                  {media}
                </div>
              );
            })}
          </div>
        </div>
        {useNavigation ? (
          <div
            className={clsx(
              fullWidth && 'mr-[12.5vw] md:mr-[10vw] md:mr-[8.3vw]',
              'mt-[20px] sm:mt-[30px] lg:mt-[40px]',
            )}
          >
            <div className="w-full relative">
              <div className="h-[3px] bg-[#DC8633] opacity-30"></div>
              <div
                ref={scrollThumb}
                className="h-[2px] bg-[#DC8633] absolute top-1/2 left-0 -translate-y-1/2
                  drop-shadow-[0px_0px_10px_#E04613]"
              ></div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

HorizontalScroller.propTypes = { images: arrayOf(object), useNavigation: bool, fullWidth: bool };

export default React.memo(HorizontalScroller);
